/**
 * Helper utilities for React Router
 */

/**
 * Clean hash fragments from URLs, especially useful for Stripe redirects
 * which can add fragments that interfere with React Router (hash router)
 * @returns {boolean} true if URL was changed
 */
export const cleanHashFromUrl = () => {
  if (window.location.hash) {
    const url = new URL(window.location.href);
    // Preserve the query string but remove the hash fragment
    const cleanUrl = `${url.origin}${url.pathname}${url.search}`;
    window.history.replaceState(null, document.title, cleanUrl);
    return true;
  }
  return false;
};

/**
 * Performs a clean redirect without hash fragments
 * @param {string} path - The path to redirect to
 */
export const cleanRedirect = (path) => {
  const baseUrl = window.location.origin;
  const publicUrl = process.env.PUBLIC_URL || "";
  const fullUrl = `${baseUrl}${publicUrl}${
    path.startsWith("/") ? path : `/${path}`
  }`;

  // Use replaceState to avoid adding to browser history
  window.history.replaceState(null, document.title, fullUrl);
  // Force a reload to ensure clean state
  window.location.reload();
};

/**
 * Get URL query parameters
 * @returns {URLSearchParams} URL search parameters object
 */
export const getUrlParams = () => {
  return new URLSearchParams(window.location.search);
};

/**
 * Create a clean URL without hash fragments
 * @param {string} path - The path to create a URL for
 * @returns {string} Full URL without hash fragments
 */
export const createCleanUrl = (path) => {
  const baseUrl = window.location.origin + (process.env.PUBLIC_URL || "");
  return `${baseUrl}${path.startsWith("/") ? path : `/${path}`}`;
};

/**
 * Create a proper hash router URL with the specified path and query parameters
 * @param {string} path - The path to create after the hash
 * @param {object} queryParams - Key-value pairs for query parameters
 * @returns {string} Full URL with hash routing and query parameters
 */
export const createHashRouterUrl = (path, queryParams = {}) => {
  // Start with base URL and path
  const baseUrl = window.location.origin;
  const routePath = path.startsWith("/") ? path : `/${path}`;

  // Convert query params to string
  const queryString = Object.keys(queryParams)
    .map((key) => {
      // Use encodeURIComponent for parameter values
      return `${encodeURIComponent(key)}=${encodeURIComponent(
        queryParams[key]
      )}`;
    })
    .join("&");

  // Combine everything
  return `${baseUrl}/#${routePath}${queryString ? `?${queryString}` : ""}`;
};
